import type { ErrorInfo, ReactNode } from 'react'
import { Component } from 'react'

interface Props {
  children: ReactNode
}

interface State {
  error: Error | null
  componentStack: string | null
}

/**
 * Catches render errors so one bad component does not blank the whole app.
 *
 * React unmounts the entire tree when a render throws and nothing catches it,
 * which is how a single undefined value turns into a white screen with only a
 * minified stack in the console. This keeps the error on the page, where it
 * can be read and reported, and lets the user recover without losing the tab.
 *
 * Production builds emit sourcemaps (see vite.config.ts), so the stack in the
 * console names real files rather than one-letter minified functions.
 */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, componentStack: null }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Kept in the console on purpose: it is the only record of a client-side
    // crash. Nothing about this ever reaches the server logs.
    console.error('Unhandled render error', error, info.componentStack)
    this.setState({ componentStack: info.componentStack ?? null })
  }

  render() {
    const { error, componentStack } = this.state

    if (!error) return this.props.children

    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6">
        <div className="box w-full max-w-xl p-6">
          <h1 className="text-lg font-semibold text-foreground">Something went wrong on this screen</h1>
          <p className="mt-2 text-sm text-foreground/70">
            The rest of the app is fine. Reloading usually clears it. If it keeps happening, send this message
            and what you were doing just before it.
          </p>

          <pre className="mt-4 max-h-60 overflow-auto rounded-lg border border-line bg-foreground/5 p-3 text-xs text-foreground/80">
            {error.message}
            {componentStack ? `\n${componentStack}` : ''}
          </pre>

          <div className="mt-4 flex gap-2">
            <button
              type="button"
              className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white"
              onClick={() => window.location.reload()}
            >
              Reload the page
            </button>
            <button
              type="button"
              className="rounded-lg border border-line px-4 py-2 text-sm font-medium text-foreground"
              onClick={() => {
                void navigator.clipboard.writeText(
                  `${error.message}\n\n${error.stack ?? ''}\n\n${componentStack ?? ''}`,
                )
              }}
            >
              Copy details
            </button>
          </div>
        </div>
      </div>
    )
  }
}
